// Generated by Copilot
import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
// import { setPermissions, setPermissionsLoading, setPermissionsError } from '../store/globalSlice';
// import { getUserPermissions } from '../api/resources/users';
// import type { RootState } from '../store';
// import type { Permission } from '../types/permissions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/store';
import { UserPermissions } from '@/store/slices/userPermissionSlice';

/**
 * Consolidated permissions hook with both basic and hierarchical functionality
 * Single hook approach for all permission-related logic
 */
export const usePermissions = () => {
    const { data: userpermissions, loading, error } = useAppSelector(
        (state: RootState) => state.userpermissions
    );

    // const userScope = useSelector((state: RootState) => state.auth.userScope);
    const dispatch = useAppDispatch();
    const { isAuthenticated } = useAuth0();
    const hasInitializedRef = useRef(false);

    // Basic CRUD permissions for a module (legacy/simple approach)
    const getModulePermissions = useMemo(() => {
        return (module: string) => {
            if (!userpermissions) {
                return { canView: false, canEdit: true, canCreate: false, canDelete: true };
            }

            const modulePerms = userpermissions.permissions.filter((p) => p.module === module);

            return {
                canView: modulePerms.some((p) => p.can_view),
                canEdit: modulePerms.some((p) => p.can_edit),
                canCreate: modulePerms.some((p) => p.can_add),
                canDelete: modulePerms.some((p) => p.can_delete),
            };
        };
    }, [userpermissions]);

    // Get module-level actions (for backward compatibility)
    const getModuleActions = useMemo(() => {
        return (module: string) => getModulePermissions(module);
    }, [getModulePermissions]);


    /**
     * Check if user has access to entire module (permissions with no parent_id)
     */
    const hasModuleAccess = useMemo(() => {
        return (module: string): boolean => {
            if (!userpermissions) return false;

            // Check for module-level permissions (no parent_id)
            const modulePermissions = userpermissions.permissions.filter(
                (p) => p.module === module && p.parent_id === null
            );

            return modulePermissions.length > 0 && modulePermissions.some((p) => p.can_view);
        };
    }, [userpermissions]);

    /**
     * Check if user has access to a specific category within a module
     */
    const hasCategoryAccess = useMemo(() => {
        return (module: string, category: string): boolean => {
            if (!userpermissions) return false;

            const categoryPermissions = userpermissions.permissions.filter(
                (p) => p.module === module && p.category === category
            );

            return categoryPermissions.length > 0 && categoryPermissions.some((p) => p.can_view);
        };
    }, [userpermissions]);

    /**
     * Check if user has access to a specific feature
     */
    const hasFeatureAccess = useMemo(() => {
        return (module: string, feature: string, category?: string): boolean => {
            if (!userpermissions) return false;

            let featurePermissions = userpermissions.permissions.filter(
                (p) => p.module === module && p.feature === feature
            );

            // If category is specified, filter by category as well
            if (category) {
                featurePermissions = featurePermissions.filter((p) => p.category === category);
            }

            return featurePermissions.length > 0 && featurePermissions.some((p) => p.can_view);
        };
    }, [userpermissions]);

    /**
     * Get available actions for a specific feature
     */
    const getFeatureActions = useMemo(() => {
        return (module: string, feature: string, category?: string) => {
            if (!userpermissions || userpermissions.permissions.length === 0) {
                console.log("getFeatureActions: Permissions not loaded, triggering fetch");

                // Trigger fetch but don't wait for it - return default values for now
                // if (isAuthenticated && userScope?.id && !isLoading) {
                //     // fetchPermissions();
                // }

                return { canView: true, canAdd: true, canEdit: true, canDelete: true };
            }

            let featurePermissions = userpermissions.permissions.filter(
                (p) => p.module === module && p.feature === feature
            );

            if (category) {
                featurePermissions = featurePermissions.filter((p) => p.category === category);
            }

            if (featurePermissions.length === 0) {
                return { canView: false, canAdd: false, canEdit: false, canDelete: false };
            }

            // Aggregate permissions (if any permission allows an action, user can perform it)
            return {
                canView: featurePermissions.some((p) => p.can_view),
                canAdd: featurePermissions.some((p) => p.can_add),
                canEdit: featurePermissions.some((p) => p.can_edit),
                canDelete: featurePermissions.some((p) => p.can_delete),
            };
        };
    }, [userpermissions]);

    /**
     * Get all categories available for a module
     */
    const getModuleCategories = useMemo(() => {
        return (module: string): string[] => {
            if (!userpermissions) return [];

            const modulePermissions = userpermissions.permissions.filter((p) => p.module === module);
            const categories = modulePermissions.map((p) => p.category);
            return [...new Set(categories)].filter(Boolean);
        };
    }, [userpermissions]);

    /**
     * Get all features available for a category
     */
    const getCategoryFeatures = useMemo(() => {
        return (module: string, category: string): string[] => {
            if (!userpermissions) return [];

            const categoryPermissions = userpermissions.permissions.filter(
                (p) => p.module === module && p.category === category
            );
            const features = categoryPermissions.map((p) => p.feature);
            return [...new Set(features)].filter(Boolean);
        };
    }, [userpermissions]);

    /**
     * Check if user can perform specific action on a feature
     */
    const canPerformFeatureAction = useMemo(() => {
        return (
            module: string,
            feature: string,
            action: 'view' | 'add' | 'edit' | 'delete',
            category?: string
        ): boolean => {
            const actions = getFeatureActions(module, feature, category);

            switch (action) {
                case 'view':
                    return actions.canView;
                case 'add':
                    return actions.canAdd;
                case 'edit':
                    return actions.canEdit;
                case 'delete':
                    return actions.canDelete;
                default:
                    return false;
            }
        };
    }, [getFeatureActions]);



    /**
     * Check if user has any permission for a module (legacy)
     */
    const hasAnyPermissionForModule = useMemo(() => {
        return (module: string): boolean => {
            if (!userpermissions) return false;
            return userpermissions.permissions.some((p) => p.module === module);
        };
    }, [userpermissions]);

    /**
     * Get accessible modules (legacy)
     */
    const getAccessibleModules = useMemo(() => {
        return (): string[] => {
            if (!userpermissions) return [];
            const modules = userpermissions.permissions.map((p) => p.module);
            return [...new Set(modules)];
        };
    }, [userpermissions]);

    // ==================== PERMISSION FETCHING FUNCTIONALITY ====================

    /**
     * Fetch permissions from API
     */
    // const fetchPermissions = useCallback(async () => {
    //     console.log("fetchPermissions called - checking prerequisites:", {
    //         isAuthenticated,
    //         userScope: !!userScope,
    //         userId: userScope?.id,
    //         permissionsExist: !!userpermissions,
    //         isCurrentlyLoading: loading
    //     });

    //     // Enhanced check: Don't call API if permissions already exist or if already loading
    //     if (userpermissions && userpermissions.permissions.length > 0) {
    //         console.log("fetchPermissions skipped - permissions already loaded:", userpermissions.permissions.length);
    //         return userpermissions;
    //     }

    //     if (loading) {
    //         console.log("fetchPermissions skipped - already loading");
    //         return null;
    //     }

    //     if (!isAuthenticated) {
    //         const errorMessage = 'User is not authenticated';
    //         console.log("fetchPermissions error:", errorMessage);
    //         dispatch(setPermissionsError(errorMessage));
    //         return null;
    //     }

    //     dispatch(setPermissionsLoading(true));
    //     dispatch(setPermissionsError(null));

    //     try {

    //         const userId = userScope?.id ? userScope.id : localStorage.getItem('userID');

    //         if (!userId) {
    //             throw new Error('No user ID available');
    //         }
    //         console.log("userid", userId);
    //         if(userpermissions && userpermissions.permissions.length > 0){
    //             return userpermissions;
    //         }

    //         const response = await getUserPermissions(userId);
    //         console.log("response", response);
    //         if (response.success && response.permissions) {
    //             dispatch(setPermissions(response.permissions));
    //             return response.permissions;
    //         } else {
    //             throw new Error('Failed to fetch permissions from server');
    //         }
    //     } catch (err) {
    //         const errorMessage = err instanceof Error ? err.message : 'Failed to fetch permissions';
    //         console.log("fetchPermissions error:", errorMessage);
    //         dispatch(setPermissionsError(errorMessage));
    //         return null;
    //     } finally {
    //         dispatch(setPermissionsLoading(false));
    //     }
    // }, [isAuthenticated, dispatch, userScope]);



    /**
     * Auto-fetch permissions if not loaded (useEnsurePermissions functionality)
     */
    // const ensurePermissionsLoaded = useCallback(() => {
    //     console.log("ensurePermissionsLoaded called - checking conditions:", {
    //         hasPermissions: !!(userpermissions && userpermissions.permissions.length > 0),
    //         loading,
    //         isAuthenticated,
    //         hasUserId: !!userScope?.id,
    //         hasInitialized: hasInitializedRef.current
    //     });

    //     // Enhanced conditions: check for actual permissions data, not just truthy
    //     if ((!userpermissions || userpermissions.permissions.length === 0) && !loading && isAuthenticated && userScope?.id) {
    //         console.log("ensurePermissionsLoaded - calling fetchPermissions");
    //         // fetchPermissions();
    //     } else {
    //         console.log("ensurePermissionsLoaded - skipping, conditions not met");
    //     }
    // }, [userpermissions, loading, isAuthenticated, userScope]);

    // Auto-fetch permissions on component mount if not loaded (only once)
    // useEffect(() => {
    //     console.log("usePermissions useEffect triggered - checking conditions:", {
    //         hasPermissions: !!(userpermissions && userpermissions.permissions.length > 0),
    //         loading,
    //         isAuthenticated,
    //         hasInitialized: hasInitializedRef.current,
    //         hasUserId: !!userScope?.id
    //     });

    //     // Enhanced conditions: more robust checks to prevent duplicate API calls
    //     if ((!userpermissions || userpermissions.permissions.length === 0) &&
    //         !loading &&
    //         isAuthenticated &&
    //         !hasInitializedRef.current &&
    //         userScope?.id) {

    //         hasInitializedRef.current = true;
    //         // fetchPermissions();
    //     } else {
    //         console.log("usePermissions useEffect - skipping initialization:", {
    //             reason: userpermissions && userpermissions.permissions.length > 0 ? "already have permissions" :
    //                    loading ? "already loading" :
    //                    !isAuthenticated ? "not authenticated" :
    //                    hasInitializedRef.current ? "already initialized" :
    //                    !userScope?.id ? "no user ID" : "unknown"
    //         });
    //     }
    // }, [userpermissions, loading, isAuthenticated, userScope, fetchPermissions]);

    return {
        // Basic/Legacy API (for backward compatibility)
        getModulePermissions,
        getModuleActions,
        hasAnyPermissionForModule,
        getAccessibleModules,

        // Hierarchical API
        hasModuleAccess,
        hasCategoryAccess,
        hasFeatureAccess,
        getFeatureActions,
        getModuleCategories,
        getCategoryFeatures,
        canPerformFeatureAction,
        rawPermissions: userpermissions,
        userpermissions, // Raw permissions data

        // Fetching functionality (from useFetchPermissions & useEnsurePermissions)
        // fetchPermissions,

        // State
        loading,
        hasError: !!error,
        error,
        isLoaded: userpermissions !== null,
        isAuthenticated,
    };
};

// ==================== LEGACY SPECIFIC HOOKS ====================

/**
 * Custom hook for checking module access (legacy)
 * Generated by Copilot
 */
export const useModuleAccess = (module: string) => {
    const { hasModuleAccess } = usePermissions();
    return hasModuleAccess(module);
};

/**
 * Custom hook for getting available actions for a module/feature (legacy)
 */
export const useModuleActions = (module: string, feature?: string) => {
    const { getModuleActions, getFeatureActions } = usePermissions();

    if (feature) {
        return getFeatureActions(module, feature);
    }

    return getModuleActions(module);
};

// Export type for the hook's return value
export type UsePermissionsReturn = ReturnType<typeof usePermissions>;
